import forceArray from 'lodash/castArray';
import { useEffect } from 'react';
import { connect } from 'react-redux';
import { Field as ReduxField } from 'redux-form';

import styles from './index.module.less';

import CheckField from '$Fields/CheckField/CheckField-css';
import CheckMultiField from '$Fields/CheckMultiField/CheckMultiField';
import DateField from '$Fields/DateField/DateField';
import HiddenField from '$Fields/HiddenField/HiddenField';
import QRCodeField from '$Fields/QRCodeField/QRCodeField';
import RadioButtonField from '$Fields/RadioButtonField/RadioButtonField-css';
import ReadOnlyField from '$Fields/ReadOnlyField';
import SelectField from '$Fields/SelectField/SelectField-css';
import RichTextField from '$Fields/TextField/RichTextField';
import TextAreaField from '$Fields/TextField/TextArea-css';
import TextField from '$Fields/TextField/TextField-css';
import Button from '$Molecules/Buttons/Button';
import { fieldExtraMatch, flexibleDate, isDateFieldSupported } from '$Utils';
import {
    formatDisplayPhone,
    normalizeCSSID,
    normalizePhone,
    normalizeSocialSecurity,
    numbers
} from '$Validation/Normalizer';
import {
    isEmail,
    isPhone,
    isReasonableDate,
    maxLength,
    maxLengthNotRequired,
    maxValue,
    minValue,
    notEmpty,
    onlyExclusiveValues
} from '$Validation/i18n-validator';

var components = {
    boolean: CheckField,
    checkboxmulti: CheckMultiField,
    'multiple choice': CheckMultiField,
    date: DateField,
    email: TextField,
    hidden: HiddenField,
    content: ReadOnlyField,
    readonly: ReadOnlyField,
    radiocheckbox: CheckField,
    select: SelectField,
    choice: RadioButtonField,
    integer: TextField,
    qrcode: QRCodeField,
    tel: TextField,
    text: TextField,
    string: TextField,
    textarea: TextAreaField
};

export const handleContinue = () => {
    //focus/blur triggers async validation, which triggers
    // partial form entry submission.
    // NEEDS TEST COVERAGE
    document.getElementById('bottom-partial-trigger').focus();
    document.getElementById('bottom-partial-trigger').blur();
};

export const fieldDescription = props => {
    const {
        partialIsSubmitting,
        change,
        field,
        invalid,
        formValues = {},
        style,
        initialValue,
        language
    } = props;
    const {
        choices,
        name,
        raw_data,
        type,
        fieldExtras = {}, //already parsed in the Duck
        help_text
    } = field;

    let { label, required, placeholder } = field;
    const id = normalizeCSSID(name);

    if (field.isSectionBreak) {
        const continueValues = {
            'en-US': 'Continue',
            es: 'Seguir'
        };
        return {
            Component: Button,
            props: {
                id: `break-${id}`,
                key: `break-${id}`,
                element: 'a',
                onClick: () => {
                    //NEEDS TEST COVERAGE
                    if (invalid) return;
                    handleContinue();
                },
                disabled: invalid,
                loading: partialIsSubmitting,
                style: Object.assign({}, { marginTop: '32px' }, style),
                text:
                    raw_data.button_text ||
                    continueValues[language] ||
                    'Continue'
            }
        };
    }

    if (type === 'content' && raw_data) {
        let { content_style, text, name } = raw_data;
        if (language !== 'en-US') {
            if (fieldExtras[`label_${language}`]?.triggerValue) {
                text = fieldExtras[`label_${language}`].triggerValue;
            }
        }
        // Safe to eval, has been cleaned on the backend
        if (content_style === 'rich') {
            return {
                Component: RichTextField,
                props: {
                    name: `${content_style}-${name}`,
                    id: `${content_style}-${name}`,
                    key: `${content_style}-${name}`,
                    text,
                    type
                }
            };
        }
        return {
            Component: content_style === 'header' ? 'h3' : 'p',
            props: {
                name: `${content_style}-${name}`,
                id: `${content_style}-${name}`,
                key: `${content_style}-${name}`,
                text,
                type,
                className: `content-${content_style}`
            }
        };
    }

    let component = components[type] || components['text'];
    let validation = [];
    let exclusiveChoices = undefined;
    let format = null;
    let normalize = null;
    let onChange = null;
    let cols;
    let disabled;
    let useOther;

    if (fieldExtras.show && fieldExtras.hide) {
        //NEEDS TEST COVERAGE
        console.error("Field can't have both 'show' and 'hide' extras");
        return;
    }
    // if there is any initial value, never hide
    if (fieldExtras.show && !initialValue) {
        const showMatch = fieldExtraMatch(fieldExtras.show, formValues);
        //If not a match
        if (!showMatch) return null;
    }

    // if there is any initial value, never hide
    if (fieldExtras.hide && !initialValue) {
        const hideMatch = fieldExtraMatch(fieldExtras.hide, formValues);
        if (hideMatch) return null;
    }

    if (fieldExtras.disabled) {
        const disabledMatch = fieldExtraMatch(fieldExtras.disabled, formValues);
        if (disabledMatch) disabled = true;
    }

    if (fieldExtras.required) {
        //`required` variable sets field required
        required = fieldExtraMatch(fieldExtras.required, formValues);
    }

    if (fieldExtras.changeValue) {
        onChange = e => {
            const newValue = e.target ? e.target.defaultValue : e;
            const rules = fieldExtras.changeValue;
            const triggerValue = forceArray(rules.triggerValue);
            // NEEDS TEST COVERAGE
            if (triggerValue.includes(newValue)) {
                change(rules.field, rules.value);
            }
        };
    }

    if (fieldExtras.copyValue) {
        onChange = e => {
            const newValue = e.target ? e.target.defaultValue : e;
            const rules = fieldExtras.copyValue;
            // NEEDS TEST COVERAGE
            if (newValue) {
                change(rules.field, newValue);
            }
        };
    }

    const { exclusiveValues } = fieldExtras;

    if (exclusiveValues) {
        validation.push(onlyExclusiveValues);
        exclusiveChoices = exclusiveValues;
    }

    if (required) validation.push(notEmpty);

    if (type === 'date') {
        if (required) validation.push(isReasonableDate);
        if (!isDateFieldSupported()) {
            normalize = value => flexibleDate(value).format('YYYY-MM-DD');
        }
    }

    if (type === 'tel' || name === 'phone_number') {
        if (required) validation.push(isPhone);
        format = formatDisplayPhone;
        normalize = normalizePhone;
    }
    if (type === 'email') {
        if (required) validation.push(isEmail);
    }

    // Needs test coverage
    if (type === 'checkbox' && choices) {
        component = components['checkboxmulti'];
        cols = choices.length > 12 ? 3 : 2;
    }
    let finalType = type;

    if (type === 'select') {
        useOther = true;
    }

    if (type === 'boolean') {
        finalType = 'checkbox';
    }
    if (type === 'integer') {
        finalType = 'number';
        normalize = numbers;

        if (field.min_value) {
            const minValidate = minValue(field.min_value);
            validation.push(minValidate);
        }
        if (field.max_value) {
            validation.push(maxValue(field.max_value));
        }
        // For number fields, use JS validation for max & min;
        // Text inputs have a native property to handle this.
        if (field.max_length) {
            if (field.required) {
                validation.push(maxLength(field.max_length));
            } else {
                validation.push(maxLengthNotRequired(field.max_length));
            }
        }
    }
    if (type === 'text' && name === 'social_security_number') {
        normalize = normalizeSocialSecurity;
        placeholder = placeholder || 'XXX-XX-XXXX';
    }

    if (type === 'readonly' && name === 'visit_identifier') {
        component = components.qrcode;
    }

    const checkTypes = ['multiple choice', 'checkbox'];

    let kind = null;

    if (checkTypes.includes(finalType)) kind = 'checkbox';
    if (finalType === 'radiocheckbox') kind = 'radio';

    const maxLengthText =
        type === 'text' || type === 'textarea' ? field.max_length : null;

    const minLengthText =
        type === 'text' || type === 'textarea' ? field.min_length : null;

    let finalChoices = choices;
    let finalLabel = label;
    let finalHelpText = help_text;
    let finalPlaceholder = placeholder;
    if (language !== 'en-US') {
        if (fieldExtras[`label_${language}`]?.triggerValue) {
            finalLabel = fieldExtras[`label_${language}`].triggerValue;
        }
        if (fieldExtras[`help_text_${language}`]?.triggerValue) {
            finalHelpText = fieldExtras[`help_text_${language}`].triggerValue;
        }
        if (fieldExtras[`placeholder_${language}`]?.triggerValue) {
            finalPlaceholder =
                fieldExtras[`placeholder_${language}`].triggerValue;
        }
        if (fieldExtras[`choices_${language}`]?.value) {
            const rawChoices = fieldExtras[`choices_${language}`].value;
            finalChoices = rawChoices.map(choice => {
                if (!choice.includes('|')) {
                    return [choice, choice];
                }
                const splitChoice = choice.split('|');
                if (splitChoice.length !== 2) {
                    return [choice, choice];
                }
                const value = splitChoice[0];
                const label = splitChoice[1];
                return [value, label];
            });
        }
    }

    let finalProps = {
        choices: finalChoices,
        cols,
        component,
        data: { label: required ? finalLabel + '*' : finalLabel },
        disabled,
        exclusiveChoices,
        format,
        help_text: finalHelpText,
        hideLabel: finalType === 'hidden',
        htmlLabel: fieldExtras.htmlLabel,
        id,
        key: `field-${name}`,
        kind,
        labelCase: 'lowercase',
        maxLength: maxLengthText,
        minLength: minLengthText,
        name,
        normalize,
        onChange,
        placeholder: finalPlaceholder,
        style,
        type: finalType,
        useOther,
        validate: validation
    };

    return {
        Component: ReduxField,
        props: finalProps
    };
};

export const DynamicField = rawProps => {
    const {
        change,
        currentValue,
        initialValue,
        field: { name }
    } = rawProps;

    const description = fieldDescription(rawProps);
    // false (bool) could be a valid value; we want to check for
    // empty string or undefined/null
    const noCurrentValue = currentValue == null || currentValue === '';
    const { disabled } = description ? description.props : false;

    useEffect(() => {
        // if the description changes from null, i.e. when rendered
        // Or if it becomes disabled
        if (!description || !!disabled) {
            // When removed from render, clear
            change(name, '');
        } else {
            if (noCurrentValue && initialValue) {
                change(name, initialValue);
            }
        }
    }, [!!description, disabled]);

    if (!description) return null;

    const { Component, props } = description;

    //Test coverage
    const rendered = () => {
        if (props.type === 'content') {
            return (
                <Component id={props.id} className={styles[props.className]}>
                    {props.text}
                </Component>
            );
        }
        return <Component {...props} />;
    };

    return (
        <div className={styles['field-visible']} key={`${name}-container`}>
            {rendered()}
        </div>
    );
};

export const mapStateToProps = (state, ownProps) => {
    const {
        formName,
        field: { name = '' }
    } = ownProps;

    const activeForm = state.activeForm || {};

    const { initialValues = {} } = activeForm;
    const initialValue = initialValues[name] || null;
    let formValues = {};
    if (state.form && state.form[formName] && state.form[formName].values) {
        formValues = state.form[formName].values;
    }
    const currentValue = formValues[name] || null;

    return Object.assign({}, ownProps, {
        formValues,
        currentValue,
        initialValue
    });
};

export default connect(mapStateToProps)(DynamicField);
